import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  selectStorageLocationSetupError,
  selectStorageLocationSetupIsLoading,
  StorageLocationsSetupActions,
} from '@luggagehero/dashboard-storage-locations-setup-data-access';
import { AppConfig } from '@luggagehero/shared/app-settings/data-access';
import { Config } from '@luggagehero/shared/environment';
import { Address, BookableStorageLocation, IPlacesSearch, Place } from '@luggagehero/shared/interfaces';
import { PlacesSearchComponent } from '@luggagehero/shared/places/feature-places-search';
import { SharedGoogleMapsService } from '@luggagehero/shared/services/google-maps';
import {
  SharedUiButtonComponent,
  SharedUiCheckboxComponent,
  SharedUiInputComponent,
  SharedUiLabelComponent,
  SharedUiPageContentComponent,
  SharedUiSpinnerComponent,
  SharedUiTextAreaComponent,
} from '@luggagehero/shared/ui';
import { TranslatePipe } from '@luggagehero/shared/ui-pipes';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

interface AddressForm {
  street: string;
  city: string;
  postal: string;
  country: string;
  countryCode: string;
}

interface AccountForm {
  email: string;
  phoneNumber: string;
  name: string;
}

interface LocationForm {
  name: string;
  address: AddressForm;
  accessCode: string;
  storageRoomAccessText: string;
  capacity: string;
  vat: string;
  location: {
    coordinates: number[];
    type: 'Point';
  };
}

@Component({
  selector: 'lh-dashboard-feature-storage-locations-setup',
  standalone: true,
  imports: [
    CommonModule,
    PlacesSearchComponent,
    ReactiveFormsModule,
    SharedUiButtonComponent,
    SharedUiInputComponent,
    SharedUiLabelComponent,
    SharedUiPageContentComponent,
    TranslateModule,
    TranslatePipe,
    SharedUiSpinnerComponent,
    SharedUiTextAreaComponent,
    SharedUiCheckboxComponent,
  ],
  templateUrl: './dashboard-feature-storage-locations-setup.component.html',
  styleUrls: ['./dashboard-feature-storage-locations-setup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardFeatureStorageLocationsSetupComponent implements OnDestroy {
  @ViewChild('placesSearch') public placesSearch?: IPlacesSearch;

  public isTyping = false;
  public selectedLocation: Place | null = null;
  public isNotOnGoogleMaps = false;
  public nativeLookAndFeel = true;
  public accountLocationForm: FormGroup;

  get dxAllowFillForm(): boolean {
    return !Config.isProduction;
  }

  private readonly cd = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly googleMapsService = inject(SharedGoogleMapsService);

  public error = this.store.selectSignal(selectStorageLocationSetupError);
  public isLoading = this.store.selectSignal(selectStorageLocationSetupIsLoading);
  private subscriptions: Subscription[] = [];
  public createdStorageLocation: BookableStorageLocation | null = null;
  private _showSuccessUI = false;

  public get showSuccessUI(): boolean {
    return this._showSuccessUI;
  }

  public set showSuccessUI(value: boolean) {
    this._showSuccessUI = value;
    this.cd.markForCheck();
  }

  onCreateAnother(): void {
    this.showSuccessUI = false;
    this.accountLocationForm.reset();
  }

  constructor() {
    this.accountLocationForm = this.createForm();
    this.subscriptions.push(
      this.actions$
        .pipe(ofType(StorageLocationsSetupActions.createHotelsHeroStorageLocationSuccess))
        .subscribe(({ storageLocation }) => {
          this.createdStorageLocation = storageLocation;
          this.showSuccessUI = true;
          this.accountLocationForm.reset();
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  private createVATValidators(hasNoVatNumber: boolean) {
    return [
      (control: AbstractControl) => (hasNoVatNumber ? null : Validators.required(control)),
      (control: AbstractControl) => (hasNoVatNumber ? null : Validators.pattern('^[a-zA-Z0-9]*$')(control)),
    ].filter(Boolean);
  }
  private createForm(): FormGroup {
    return this.fb.group({
      account: this.fb.group({
        email: [
          '',
          [
            (control: AbstractControl) => Validators.required(control),
            (control: AbstractControl) => Validators.pattern(AppConfig.EMAIL_REGEX)(control),
          ],
        ],
        phoneNumber: [
          '',
          [
            (control: AbstractControl) => Validators.required(control),
            (control: AbstractControl) => Validators.pattern(AppConfig.PHONE_REGEX)(control),
          ],
        ],
        name: ['', [(control: AbstractControl) => Validators.required(control)]],
      }),
      location: this.fb.group({
        name: ['', [(control: AbstractControl) => Validators.required(control)]],
        address: this.fb.group({
          street: ['', [(control: AbstractControl) => Validators.required(control)]],
          city: ['', [(control: AbstractControl) => Validators.required(control)]],
          postal: ['', [(control: AbstractControl) => Validators.required(control)]],
          country: ['', [(control: AbstractControl) => Validators.required(control)]],
          countryCode: ['', [(control: AbstractControl) => Validators.required(control)]],
        }),
        accessCode: [''],
        storageRoomAccessText: [''],
        capacity: [
          '',
          [
            (control: AbstractControl) => Validators.required(control),
            (control: AbstractControl) => Validators.pattern('^[0-9]*$')(control),
          ],
        ],
        vat: ['', this.createVATValidators(this.hasNoVatNumber)],
        location: this.fb.group({
          coordinates: this.fb.array([0, 0]),
          type: ['Point'],
        }),
      }),
    });
  }

  private updateVATValidators(hasNoVatNumber: boolean) {
    const vatControl = this.accountLocationForm.get('location.vat');
    vatControl?.clearValidators();
    vatControl?.setValidators(this.createVATValidators(hasNoVatNumber));
    vatControl?.updateValueAndValidity();
  }

  private _hasNoVatNumber = false;
  private previousVatNumber: string | null = null;

  get hasNoVatNumber(): boolean {
    return this._hasNoVatNumber;
  }
  set hasNoVatNumber(value: boolean) {
    if (value) {
      this.previousVatNumber = this.accountLocationForm?.get('location.vat')?.value as string;
      this.accountLocationForm.patchValue({
        location: {
          vat: '',
        },
      });
    } else if (this.previousVatNumber) {
      this.accountLocationForm.patchValue({
        location: {
          vat: this.previousVatNumber,
        },
      });
      this.previousVatNumber = null;
    }
    this._hasNoVatNumber = value;

    this.updateVATValidators(this.hasNoVatNumber);
    this.cd.markForCheck();
  }

  public onPlaceChanged(place: Place): void {
    this.selectedLocation = place;

    if (place) {
      this.accountLocationForm.patchValue({
        location: {
          name: place.name,
          address: {
            street: place.address.street,
            city: place.address.city,
            postal: place.address.postal,
            country: place.address.country,
            countryCode: place.address.countryCode,
          },
          location: {
            coordinates: place.address.location,
            type: 'Point',
          },
        },
      });
    }
  }

  startTyping() {
    this.isTyping = true;
    this.placesSearch?.startTyping();
  }

  stopTyping(): void {
    this.isTyping = false;
    this.placesSearch?.clearAll();
    this.cd.markForCheck();
  }

  private formatVal(val, addCommaEnd = true): string {
    return `${val ? val + (addCommaEnd ? ', ' : '') : ''}`;
  }

  singleLineAddress(): string {
    const { location } = this.accountLocationForm.value as {
      account: AccountForm;
      location: LocationForm;
    };

    const street = this.formatVal(location.address.street);
    const zip = this.formatVal(location.address.postal);
    const city = this.formatVal(location.address.city);
    const country = this.formatVal(location.address.country, false);
    const singleLine = `${street} ${zip} ${city} ${country}`;
    return singleLine;
  }

  private updateAddress(value: Address) {
    if (!value) {
      return;
    }

    this.accountLocationForm.patchValue(
      {
        location: {
          address: {
            street: value.street,
            city: value.city,
            postal: value.postal,
            country: value.country,
            countryCode: value.countryCode,
          },
          location: {
            coordinates: value.location,
            type: 'Point',
          },
        },
      },
      {
        emitEvent: false,
      },
    );

    this.cd.markForCheck();
  }

  geocodeAdress(): void {
    this.googleMapsService.geocode({ address: this.singleLineAddress() }).subscribe((res) => {
      const place = this.googleMapsService.parseGeocoderResult(res[0]);
      this.updateAddress(place.address);
    });
  }

  public onSubmit(): void {
    if (this.accountLocationForm.invalid) {
      return;
    }

    const { account, location } = this.accountLocationForm.value as {
      account: AccountForm;
      location: LocationForm;
    };

    const payload = {
      account: {
        email: account.email,
        phoneNumber: account.phoneNumber,
        name: account.name,
      },
      storageLocation: {
        name: location.name,
        address: location.address,
        accessCode: location.accessCode || '',
        storageRoomAccessText: location.storageRoomAccessText,
        capacity: {
          normal: Number(location.capacity),
          hand: 0,
        },
        place: this.selectedLocation ? { id: this.selectedLocation.id, name: this.selectedLocation.name } : null,
        vat: location.vat,
        location: location.location,
      },
    };

    this.store.dispatch(StorageLocationsSetupActions.createHotelsHeroStorageLocation({ payload }));
  }

  dxFillForm(): void {
    const suffix = generateTimestampString();
    this.accountLocationForm.patchValue({
      account: {
        email: generateRandomEmail(suffix),
        phoneNumber: '+34123456789',
        name: 'John Doe',
      },
      location: {
        name: generateRandomStorageLocationName(suffix),
        address: {
          street: '60 Rambla dels Caputxins',
          city: 'Barcelona',
          postal: '08002',
          country: 'Spain',
          countryCode: 'es',
        },
        accessCode: '123456',
        storageRoomAccessText: '',
        capacity: '1000',
        vat: 'ESX1234567X',
        location: {
          coordinates: [2.1697, 41.3879],
          type: 'Point',
        },
      },
    });
  }
}

function generateTimestampString() {
  const timestamp = new Date().toISOString().replace(/[-:.TZ]/g, ''); // Current timestamp without special characters
  const randomString = Math.random().toString(36).substring(2, 8); // Random alphanumeric string

  return `${timestamp}_${randomString}`;
}

function generateRandomStorageLocationName(suffix: string) {
  return `Storage_${suffix}`;
}

function generateRandomEmail(suffix: string) {
  return `user_${suffix}@example.com`;
}
